import { yupResolver } from '@hookform/resolvers/yup';
import { $generateHtmlFromNodes, RichTextEditor } from '@talentmesh/rte';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Button } from '@/Components/shadcn/button';
import { Spinner } from '@/Components/shadcn/spinner';
import { Card, CardContent } from '@/Components/shadcn/card';
import { useNotificationContext } from '@/Context/NotificationContext';
import { EmailTemplateType } from '@/Models/Configuration';
import UIStrings from '@/Utils/UIStrings';
import EmailTemplatePlaceholders from '../EmailTemplatePlaceholders';
import PreviewEmailDialog from './PreviewEmailDialog';
import Closure from './TemplateEditor/Closure';
import Salutation from './TemplateEditor/Salutation';
import { emailTemplateSchema, TemplateEditorForm } from './TemplateEditor/utils';
import { useAccountSettings, useSaveEmailTemplate } from '@/api/useAccountSettings';
import { useCompanyContext } from '@/Context/CompanyContext';
import { getEmailTemplateHtml } from '../../utils/getEmailTemplateHtml';

interface TemplateEditorProps {
    title: string;
    bodyHtml: string;
    regards: string;
    thankYouMessage: string;
    templateType: EmailTemplateType;
    setDirty: (value: boolean) => void;
}

function TemplateEditor({
    title,
    bodyHtml,
    regards,
    thankYouMessage,
    templateType,
    setDirty,
}: TemplateEditorProps): React.JSX.Element {
    const [openPreview, setOpenPreview] = useState(false);

    const { isLoading } = useAccountSettings();
    const { showSuccessToaster, showFailToaster } = useNotificationContext();
    const [currentBodyHtml, setCurrentBodyHtml] = useState<string>('');
    const { isMutating, trigger: saveEmailTemplateAsync } = useSaveEmailTemplate();
    const form = useForm({ resolver: yupResolver(emailTemplateSchema), mode: 'onSubmit' });
    const { currentCompanyProfile } = useCompanyContext();

    useEffect(() => {
        if (!isLoading) form.reset({ title, regards });
    }, [form.reset, isLoading]);

    const dialogTitle =
        templateType === EmailTemplateType.InviteTemplate
            ? UIStrings.PreviewInvitationEmail
            : UIStrings.PreviewRejectionEmail;

    const submitHandler = async (values: TemplateEditorForm) => {
        try {
            const fullBodyHtml = getEmailTemplateHtml(
                values.title,
                thankYouMessage,
                currentBodyHtml,
                values.regards,
                EmailTemplatePlaceholders.ApplicantName,
                EmailTemplatePlaceholders.RecruiterName,
                EmailTemplatePlaceholders.CompanyName,
                undefined,
                templateType !== EmailTemplateType.RejectTemplate,
            );
            await saveEmailTemplateAsync({
                type: templateType,
                title: values.title,
                bodyHtml: currentBodyHtml,
                regards: values.regards,
                templateHtml: fullBodyHtml,
                companyId: currentCompanyProfile.id,
            });
            setDirty(false);
            showSuccessToaster(UIStrings.EmailTemplateSuccessfullyUpdated);
            form.reset({ title: values.title, regards: values.regards });
        } catch (error) {
            showFailToaster();
        }
    };

    return (
        <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(submitHandler)}>
                <div className="flex flex-col gap-5">
                    <Card className="border-border bg-card overflow-hidden rounded-xl border drop-shadow-none">
                        <CardContent className="p-4 md:p-6">
                            <div className="flex flex-col items-start justify-start gap-3">
                                <Salutation thankYouMessage={thankYouMessage} />
                                <div className="flex w-full flex-col">
                                    <RichTextEditor
                                        features={['bold', 'italic', 'underline', 'ol', 'ul']}
                                        initialHtml={bodyHtml}
                                        onBlur={setDirty}
                                        onChange={(editorState, editor) => {
                                            editorState.read(() => {
                                                setCurrentBodyHtml($generateHtmlFromNodes(editor));
                                            });
                                        }}
                                        contentEditableProps={{
                                            ariaLabel: 'edit recruiter signature template',
                                            className: 'max-h-60',
                                            style: { overflowY: 'auto' },
                                        }}
                                    />
                                </div>
                                <Closure showRecruiterName={templateType !== EmailTemplateType.RejectTemplate} />
                            </div>
                        </CardContent>
                    </Card>
                    <div className="flex flex-row gap-3">
                        <Button disabled={isMutating} size="lg" type="submit">
                            {isMutating && <Spinner className="mr-2 size-4" />}
                            {UIStrings.SaveTemplate}
                        </Button>
                        <Button variant="outline" size="lg" onClick={() => setOpenPreview(true)} type="button">
                            {UIStrings.PreviewEmail}
                        </Button>
                    </div>
                    <PreviewEmailDialog
                        dialogTitle={dialogTitle}
                        thankYouMessage={thankYouMessage}
                        bodyHtml={currentBodyHtml}
                        open={openPreview}
                        onClose={() => setOpenPreview(false)}
                        templateType={templateType}
                    />
                </div>
            </form>
        </FormProvider>
    );
}

export default TemplateEditor;
